import React, {useCallback, useEffect, useState} from 'react';
import {
  View,
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  RefreshControl,
  Alert,
  Dimensions,
} from 'react-native';
import {
  Text,
  Appbar,
  ActivityIndicator,
  FAB,
  Portal,
  Dialog,
  Button,
  TextInput,
  Snackbar,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useFocusEffect, DrawerActions} from '@react-navigation/native';
import {useGalleryStore} from '../../store/useGalleryStore';
import {colors, spacing, borderRadius} from '../../theme';

const SCREEN_WIDTH = Dimensions.get('window').width;
const CARD_GAP = spacing.sm;
const CARD_WIDTH = (SCREEN_WIDTH - spacing.md * 2 - CARD_GAP) / 2;

const GalleryScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {folders, isLoading, fetchFolders, createFolder, deleteFolder} =
    useGalleryStore();

  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [snackbar, setSnackbar] = useState('');

  useFocusEffect(
    useCallback(() => {
      fetchFolders();
    }, [fetchFolders]),
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchFolders();
    setRefreshing(false);
  }, [fetchFolders]);

  const handleCreateFolder = useCallback(async () => {
    const name = folderName.trim();
    if (!name) return;
    try {
      await createFolder(name);
      setDialogVisible(false);
      setFolderName('');
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [folderName, createFolder, t]);

  const handleDeleteFolder = useCallback(
    folder => {
      if (folder.isDefault) {
        setSnackbar(t('gallery.cannotDeleteDefault'));
        return;
      }
      Alert.alert(t('common.delete'), t('gallery.deleteFolder'), [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteFolder(folder.id);
            } catch (e) {
              setSnackbar(e.response?.data?.message || t('common.error'));
            }
          },
        },
      ]);
    },
    [deleteFolder, t],
  );

  const handleOpenFolder = useCallback(
    folder => {
      navigation.navigate('GalleryFolderImages', {
        folderId: folder.id,
        folderName: folder.name,
        isDefault: folder.isDefault,
      });
    },
    [navigation],
  );

  const renderFolder = useCallback(
    ({item}) => (
      <TouchableOpacity
        style={styles.card}
        onPress={() => handleOpenFolder(item)}
        onLongPress={() => handleDeleteFolder(item)}
        activeOpacity={0.7}>
        <View style={styles.cardImageContainer}>
          {item.coverImageUrl ? (
            <Image
              style={styles.cardImage}
              source={{uri: item.coverImageUrl}}
              resizeMode="cover"
            />
          ) : (
            <View style={styles.cardPlaceholder}>
              <MaterialCommunityIcons
                name="image-multiple-outline"
                size={40}
                color={colors.textLight}
              />
            </View>
          )}
        </View>
        <View style={styles.cardInfo}>
          <Text style={styles.cardName} numberOfLines={1}>
            {item.name}
          </Text>
          <Text style={styles.cardCount}>
            {item.imageCount} {item.imageCount === 1 ? 'image' : 'images'}
          </Text>
        </View>
      </TouchableOpacity>
    ),
    [handleOpenFolder, handleDeleteFolder],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  if (isLoading && folders.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.Action icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
          <Appbar.Content
            title={t('nav.gallery')}
            titleStyle={styles.appbarTitle}
          />
        </Appbar.Header>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
        <Appbar.Content
          title={t('nav.gallery')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      <FlatList
        data={folders}
        keyExtractor={keyExtractor}
        renderItem={renderFolder}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        ListEmptyComponent={
          <View style={styles.centered}>
            <MaterialCommunityIcons
              name="folder-image"
              size={64}
              color={colors.textLight}
            />
            <Text style={styles.emptyText}>{t('gallery.noFolders')}</Text>
            <Text style={styles.emptySubtext}>{t('gallery.tapToAdd')}</Text>
          </View>
        }
      />

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={() => {
          setFolderName('');
          setDialogVisible(true);
        }}
        color={colors.white}
      />

      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}>
          <Dialog.Title>{t('gallery.createFolder')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('gallery.folderName')}
              value={folderName}
              onChangeText={setFolderName}
              mode="outlined"
              style={styles.dialogInput}
              autoFocus
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleCreateFolder}
              disabled={!folderName.trim()}
              style={styles.createButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  centered: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.md, flexGrow: 1},
  row: {justifyContent: 'space-between', marginBottom: CARD_GAP},
  card: {
    width: CARD_WIDTH,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  cardImageContainer: {
    width: '100%',
    height: CARD_WIDTH * 0.75,
  },
  cardImage: {
    width: '100%',
    height: '100%',
  },
  cardPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardInfo: {padding: spacing.sm},
  cardName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  cardCount: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  emptyText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textLight,
    marginTop: spacing.xs,
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  dialogInput: {
    backgroundColor: colors.surface,
  },
  createButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default GalleryScreen;
